import { useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components'

import { TooltipCardParentGuardProvider } from '@root/shared/ui-kit'

import { ExplorePairsHeading } from './explore-pairs-heading'

import type { AsyncStateN } from '@blade/std-lib'
import type { PairModel } from '@root/entities/pair'
import type { Error } from '@root/shared'
import type { ClassNameProps } from '@root/shared/react'

type ExploreBlockSectionProps = {
  pairsState: AsyncStateN.AsyncStateN<Error.UnknownError, Array<PairModel>>
  paused: boolean
  onPausedChange: (paused: boolean) => void
  heading: React.ReactNode
  children: React.ReactNode
  filter?: React.ReactNode
  sort?: React.ReactNode
  disabled?: boolean
  buffedCount: number
} & ClassNameProps

// Pad the feed rect so the pointer crossing the section's edges (or sitting on
// the heading badge) still reads as "on the feed".
const REGION_PADDING = 8
// Debounce before unpausing once the pointer leaves the feed/overlay region.
const UNPAUSE_DELAY_MS = 80
// How long a closed tooltip's rect keeps the feed paused, so a transient close
// (e.g. the card's own close-on-enter heuristic firing as the pointer reaches
// its closeArea) doesn't unpause and hide the New Pairs badge.
const OVERLAY_RECT_GRACE_MS = 300
// Radix portals popover/hover-card content inside this wrapper. Tooltip bodies
// (closeArea/clickArea) therefore live outside the feed in the DOM.
const POPPER_WRAPPER_SELECTOR = '[data-radix-popper-content-wrapper]'

export const ExploreBlock: React.FC<ExploreBlockSectionProps> = ({
  pairsState,
  paused,
  onPausedChange,
  heading,
  children,
  filter,
  sort,
  disabled,
  buffedCount,
}) => {
  const bodyRef = useRef<HTMLDivElement>(null)
  const nestedOverlayOpenRef = useRef(false)
  // Tooltip bodies are portaled outside the feed, so `:hover`/containment can't
  // tell whether the pointer is still "on the feed surface" while a card is
  // open. We keep pause based on geometry instead, and hold the last open
  // card's rect for a short grace window after it closes — that bridges the
  // card's own close-on-enter heuristic firing as the pointer reaches its
  // closeArea, which would otherwise unpause and hide the New Pairs badge.
  const overlayRectRef = useRef<DOMRect | undefined>(undefined)
  const overlayExpiresRef = useRef(0)

  const latestRef = useRef({ onPausedChange })
  latestRef.current = { onPausedChange }

  const handleNestedOverlayOpenChange = useCallback((open: boolean) => {
    if (open) {
      // Snapshot the portaled card so a transient close keeps the feed paused.
      requestAnimationFrame(() => {
        const el = [
          ...document.querySelectorAll<HTMLElement>(POPPER_WRAPPER_SELECTOR),
        ].at(-1)
        if (el) {
          overlayRectRef.current = el.getBoundingClientRect()
        }
      })
      return
    }
    overlayExpiresRef.current = performance.now() + OVERLAY_RECT_GRACE_MS
  }, [])

  // While paused, drive unpause from pointer geometry — resilient to the feed's
  // realtime row churn and to tooltip bodies portaled outside the feed, neither
  // of which produce reliable mouseleave events.
  useEffect(() => {
    if (!paused) {
      return
    }

    let timer: ReturnType<typeof setTimeout> | undefined
    const handleMove = (event: PointerEvent) => {
      const { clientX: x, clientY: y } = event
      const inRegion =
        nestedOverlayOpenRef.current ||
        inRect(
          x,
          y,
          bodyRef.current?.getBoundingClientRect(),
          REGION_PADDING,
        ) ||
        (performance.now() < overlayExpiresRef.current &&
          inRect(x, y, overlayRectRef.current))
      if (inRegion) {
        clearTimeout(timer)
        timer = undefined
      } else if (!timer) {
        timer = setTimeout(
          () => latestRef.current.onPausedChange(false),
          UNPAUSE_DELAY_MS,
        )
      }
    }

    document.addEventListener('pointermove', handleMove, { passive: true })
    return () => {
      document.removeEventListener('pointermove', handleMove)
      clearTimeout(timer)
    }
  }, [paused])

  const handleSectionEnter = () => {
    const pairs = pairsState.value ?? []
    if (pairs.length > 0 && !paused) {
      onPausedChange(true)
    }
  }

  return (
    <Root>
      <ExplorePairsHeading
        heading={heading}
        paused={paused}
        filter={filter}
        sort={sort}
        disabled={disabled}
        buffedCount={buffedCount}
      />
      <Body onMouseEnter={handleSectionEnter} ref={bodyRef}>
        <TooltipCardParentGuardProvider
          nestedOverlayOpenRef={nestedOverlayOpenRef}
          onNestedOverlayOpenChange={handleNestedOverlayOpenChange}
        >
          {children}
        </TooltipCardParentGuardProvider>
      </Body>
    </Root>
  )
}

const inRect = (x: number, y: number, rect: DOMRect | undefined, pad = 0) =>
  rect !== undefined &&
  x >= rect.left - pad &&
  x <= rect.right + pad &&
  y >= rect.top - pad &&
  y <= rect.bottom + pad

const Root = styled.section`
  background: var(--appearanceA-5);
  border-radius: 12px;

  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  height: 100%;
  min-height: 0;

  overflow-x: hidden;
`

const Body = styled.div`
  display: grid;
  position: relative;
  min-height: 0;
`
